import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { createNotification } from '../redux/slices/notificationsSlice';

/**
 * Service für das Monitoring von pre-accepted Fahrten
 * Überprüft regelmäßig ob geplante Fahrten rechtzeitig vorbereitet werden
 * Warnt 5 Minuten VOR der geplanten Zeit und bei Überfälligkeit
 */
class RideMonitoringService {
  constructor() {
    this.monitoringInterval = null;
    this.checkInterval = 30000; // Überprüfe alle 30 Sekunden
    this.alertThreshold = 300000; // 5 Minuten in Millisekunden
    this.dispatch = null;
    this.currentUserId = null;
    this.soundEnabled = true;
    this.overdueRides = new Set(); // Track bereits gemeldete überfällige Fahrten
    this.audioContext = null; // AudioContext für Sound-Benachrichtigungen
  }

  /**
   * Startet das Monitoring-System
   * @param {Function} dispatch - Redux dispatch function
   * @param {string} userId - ID des aktuellen Benutzers
   */
  startMonitoring(dispatch, userId) {
    this.dispatch = dispatch;
    this.currentUserId = userId;
    
    console.log('Starte Fahrt-Monitoring für pre-accepted Fahrten (5 Min. Vorwarnung)...');
    
    // Stoppe eventuell laufendes Monitoring
    this.stopMonitoring();
    
    // Erste Überprüfung sofort
    this.checkOverdueRides();
    
    // Regelmäßige Überprüfung starten
    this.monitoringInterval = setInterval(() => {
      this.checkOverdueRides();
    }, this.checkInterval);
  }

  /**
   * Stoppt das Monitoring-System
   */
  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
      console.log('Fahrt-Monitoring gestoppt.');
    }
    
    // Reset der überfälligen Fahrten
    this.overdueRides.clear();
  }

  /**
   * Überprüft alle pre-accepted Fahrten auf Überfälligkeit
   */
  async checkOverdueRides() {
    try {
      if (!this.dispatch || !this.currentUserId) {
        console.warn('Monitoring nicht korrekt initialisiert');
        return;
      }

      const now = new Date();
      const alertTime = new Date(now.getTime() + this.alertThreshold); // PLUS statt MINUS!
      
      // Query für pre-accepted Fahrten die in den nächsten 5 Minuten starten sollen
      const overdueQuery = query(
        collection(db, 'rides'),
        where('status', '==', 'pre_accepted'),
        where('scheduledTime', '<=', Timestamp.fromDate(alertTime))
      );

      const querySnapshot = await getDocs(overdueQuery);
      
      if (querySnapshot.empty) {
        // Keine überfälligen Fahrten gefunden - alle gemeldeten als behoben markieren
        this.overdueRides.clear();
        return;
      }

      let newOverdueCount = 0;
      
      querySnapshot.docs.forEach(doc => {
        const rideData = doc.data();
        const rideId = doc.id;
        
        // Nur neue überfällige Fahrten melden
        if (!this.overdueRides.has(rideId)) {
          this.createOverdueNotification(rideId, rideData);
          this.overdueRides.add(rideId);
          newOverdueCount++;
        }
      });

      if (newOverdueCount > 0) {
        console.log(`${newOverdueCount} neue Fahrt(en) die bald starten oder überfällig sind erkannt`);
        
        // Sound-Warnung abspielen
        if (this.soundEnabled) {
          this.playAlertSound();
        }
      }

      // Entferne Fahrten die nicht mehr überfällig sind
      const currentOverdueIds = new Set(querySnapshot.docs.map(doc => doc.id));
      this.overdueRides.forEach(rideId => {
        if (!currentOverdueIds.has(rideId)) {
          this.overdueRides.delete(rideId);
        }
      });

    } catch (error) {
      console.error('Fehler beim Überprüfen überfälliger Fahrten:', error);
    }
  }

  /**
   * Erstellt eine Benachrichtigung für eine Fahrt die bald startet
   * @param {string} rideId - ID der Fahrt die bald startet
   * @param {Object} rideData - Fahrtdaten
   */
  createOverdueNotification(rideId, rideData) {
    const scheduledTime = rideData.scheduledTime.toDate();
    const now = new Date();
    const minutesUntilStart = Math.ceil((scheduledTime - now) / (1000 * 60));
    
    const timeString = scheduledTime.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const customerName = rideData.customerName || 'Unbekannter Kunde';
    const driverName = rideData.driverName || 'Fahrer';
    
    let title, message;
    
    if (minutesUntilStart <= 0) {
      // Fahrt sollte bereits gestartet sein
      title = '🚨 Überfällige Fahrt';
      message = `Fahrt von ${customerName} um ${timeString} wurde von ${driverName} nicht rechtzeitig begonnen!`;
    } else {
      // Fahrt startet bald
      title = '⏰ Fahrt startet bald';
      message = `Fahrt von ${customerName} startet in ${minutesUntilStart} Min. um ${timeString}. Fahrer: ${driverName}`;
    }
    
    const notification = {
      user_id: this.currentUserId,
      title: title,
      message: message,
      type: 'overdue_ride',
      data: {
        rideId: rideId,
        customerId: rideData.customerId,
        driverId: rideData.driverId,
        scheduledTime: scheduledTime.toISOString(),
        minutesUntilStart: minutesUntilStart,
        priority: minutesUntilStart <= 0 ? 'critical' : 'high'
      }
    };

    // Benachrichtigung über Redux erstellen
    this.dispatch(createNotification(notification));
  }

  /**
   * Spielt einen Alarm-Sound ab
   */
  playAlertSound() {
    try {
      // Web Audio API für Alarm-Sound (nur bei Benutzerinteraktion möglich)
      if (typeof AudioContext !== 'undefined' || typeof webkitAudioContext !== 'undefined') {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        
        // Prüfe ob AudioContext bereits initialisiert ist
        if (!this.audioContext) {
          this.audioContext = new AudioContext();
        }
        
        // AudioContext muss durch Benutzerinteraktion aktiviert werden
        if (this.audioContext.state === 'suspended') {
          console.log('🔊 Audio-Kontext gesperrt - Sound wird beim nächsten Klick aktiviert');
          
          // Versuche AudioContext bei nächster Benutzerinteraktion zu aktivieren
          const enableAudio = () => {
            this.audioContext.resume().then(() => {
              console.log('🔊 Audio-Kontext aktiviert');
              this.playBeepSound();
            });
            // Event-Listener nur einmal ausführen
            document.removeEventListener('click', enableAudio);
            document.removeEventListener('keydown', enableAudio);
          };
          
          document.addEventListener('click', enableAudio, { once: true });
          document.addEventListener('keydown', enableAudio, { once: true });
          
          // Fallback-Benachrichtigung
          this.showVisualAlert();
          return;
        }
        
        // AudioContext ist bereit - Sound abspielen
        this.playBeepSound();
        
      } else {
        // Fallback: Visuelle Benachrichtigung
        this.showVisualAlert();
      }
    } catch (error) {
      console.error('Fehler beim Abspielen des Alarm-Sounds:', error);
      this.showVisualAlert();
    }
  }

  /**
   * Spielt den eigentlichen Beep-Sound ab
   */
  playBeepSound() {
    try {
      // Erstelle einen Beep-Sound
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime); // 800 Hz
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.5);
      
      // Zweiter Beep nach kurzer Pause
      setTimeout(() => {
        if (this.audioContext && this.audioContext.state === 'running') {
          const oscillator2 = this.audioContext.createOscillator();
          const gainNode2 = this.audioContext.createGain();
          
          oscillator2.connect(gainNode2);
          gainNode2.connect(this.audioContext.destination);
          
          oscillator2.frequency.setValueAtTime(600, this.audioContext.currentTime);
          oscillator2.type = 'sine';
          
          gainNode2.gain.setValueAtTime(0.3, this.audioContext.currentTime);
          gainNode2.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);
          
          oscillator2.start(this.audioContext.currentTime);
          oscillator2.stop(this.audioContext.currentTime + 0.5);
        }
      }, 200);
      
    } catch (error) {
      console.error('Fehler beim Beep-Sound:', error);
      this.showVisualAlert();
    }
  }

  /**
   * Zeigt eine visuelle Alert-Benachrichtigung als Fallback
   */
  showVisualAlert() {
    // Visuelle Desktop-Benachrichtigung (falls erlaubt)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('⚠️ Überfällige Fahrt erkannt!', {
        body: 'Eine geplante Fahrt benötigt Ihre Aufmerksamkeit.',
        icon: '/favicon.ico',
        requireInteraction: true
      });
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      // Berechtigung anfordern
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('⚠️ Überfällige Fahrt erkannt!', {
            body: 'Eine geplante Fahrt benötigt Ihre Aufmerksamkeit.',
            icon: '/favicon.ico',
            requireInteraction: true
          });
        }
      });
    } else {
      // Fallback: Console-Benachrichtigung
      console.log('🚨 ÜBERFÄLLIGE FAHRT ERKANNT! 🚨');
    }
  }

  /**
   * Aktiviert/Deaktiviert Sound-Benachrichtigungen
   * @param {boolean} enabled 
   */
  setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
    console.log(`Sound-Benachrichtigungen ${enabled ? 'aktiviert' : 'deaktiviert'}`);
  }

  /**
   * Ändert das Überprüfungsintervall
   * @param {number} intervalMs - Neues Intervall in Millisekunden
   */
  setCheckInterval(intervalMs) {
    this.checkInterval = intervalMs;
    
    // Monitoring neu starten wenn es läuft
    if (this.monitoringInterval && this.dispatch && this.currentUserId) {
      this.startMonitoring(this.dispatch, this.currentUserId);
    }
  }

  /**
   * Ändert die Schwelle für Vorwarnungen (Zeit VOR der geplanten Fahrt)
   * @param {number} thresholdMs - Neue Schwelle in Millisekunden  
   */
  setAlertThreshold(thresholdMs) {
    this.alertThreshold = thresholdMs;
    console.log(`Vorwarnungs-Schwelle auf ${thresholdMs / 1000 / 60} Minuten VOR der geplanten Zeit gesetzt`);
  }

  /**
   * Gibt aktuelle Statistiken zurück
   */
  getStats() {
    return {
      isRunning: this.monitoringInterval !== null,
      checkInterval: this.checkInterval,
      alertThreshold: this.alertThreshold,
      currentOverdueCount: this.overdueRides.size,
      soundEnabled: this.soundEnabled
    };
  }
}

// Singleton-Instanz für globale Nutzung
const rideMonitoringService = new RideMonitoringService();

export default rideMonitoringService;